import { MAIN_URL, REQUEST_URL } from "../../constant/ApplicationVariables";
import { axiosInstance } from "../useAxios/useAxios";
import { HttpStatusCode } from 'axios';

/**
 * Helper to construct URLs to avoid repetition
 */
const getUrl = (path, query = "") => {
    const queryString = query ? (query.startsWith('?') ? query : `?${query}`) : "";
    return `${MAIN_URL.url}${path}${queryString}`;
};

/**
 * Base API handler with consistent error handling
 */
const handleApiResponse = async (apiCall) => {
    try {
        const response = await apiCall;
        
        if (!response) {
            return {
                success: false,
                error: "No response received from server",
                status: 500
            };
        }

        // Success cases
        if (response.status === HttpStatusCode.Ok || 
            response.status === HttpStatusCode.Created) {
            return {
                success: true,
                data: response.data,
                message: response?.data?.message || "Operation completed successfully",
                status: response.status
            };
        }

        // Handle validation errors (406 Not Acceptable)
        if (response.status === HttpStatusCode.NotAcceptable) {
            const errorData = response?.data || {};
            const fields = errorData?.fields || {};
            
            // Transform field errors for better UI display
            const formattedErrors = {};
            Object.keys(fields).forEach((key) => {
                const fieldMap = {
                    'newName': 'name',
                    'name': 'name',
                    'description': 'description',
                    'categoryName': 'categoryName',
                    'subCategoryName': 'subCategoryName'
                };
                const uiField = fieldMap[key] || key;
                formattedErrors[uiField] = fields[key];
            });

            return {
                success: false,
                error: "Validation Error",
                fields: formattedErrors,
                message: errorData?.message || "Please check your inputs",
                status: response.status,
                rawResponse: response
            };
        }

        // Handle other error statuses
        return {
            success: false,
            error: response?.data?.message || "An error occurred",
            status: response.status,
            data: response?.data
        };

    } catch (error) {
        console.error("API Error:", error);
        
        if (error.response) {
            const status = error.response.status;
            const data = error.response.data;

            // Handle validation errors from catch block
            if (status === HttpStatusCode.NotAcceptable) {
                const fields = data?.fields || {};
                const formattedErrors = {};
                Object.keys(fields).forEach((key) => {
                    const fieldMap = {
                        'newName': 'name',
                        'name': 'name',
                        'description': 'description',
                        'categoryName': 'categoryName',
                        'subCategoryName': 'subCategoryName'
                    };
                    const uiField = fieldMap[key] || key;
                    formattedErrors[uiField] = fields[key];
                });

                return {
                    success: false,
                    error: "Validation Error",
                    fields: formattedErrors,
                    message: data?.message || "Please check your inputs",
                    status: status,
                    rawResponse: error.response
                };
            }

            return {
                success: false,
                error: data?.message || "Server error occurred",
                status: status,
                data: data
            };
        } else if (error.request) {
            return {
                success: false,
                error: "No response from server. Please check your connection.",
                status: 0
            };
        } else {
            return {
                success: false,
                error: error.message || "Request failed",
                status: 500
            };
        }
    }
};

/**
 * API Functions with consistent handling
 */
export const getProjectList_API = async () => {
    return handleApiResponse(
        axiosInstance({
            url: getUrl(REQUEST_URL.projects),
            method: "GET"
        })
    );
};

export const getProjectCategoryList_API = async () => {
    return handleApiResponse(
        axiosInstance({
            url: getUrl(REQUEST_URL.category),
            method: "GET"
        })
    );
};

export const getProjectSubcategoryList_API = async (query) => {
    return handleApiResponse(
        axiosInstance({
            url: getUrl(REQUEST_URL.category, query),
            method: "GET"
        })
    );
};

export const addProject_API = async (body) => {
    return handleApiResponse(
        axiosInstance({
            url: getUrl(REQUEST_URL.projects),
            method: "POST",
            data: body
        })
    );
};

export const getProjectStatus_API = async () => {
    return handleApiResponse(
        axiosInstance({
            url: getUrl(REQUEST_URL.projectStatus),
            method: "GET"
        })
    );
};

export const updateProject_API = async (body) => {
    return handleApiResponse(
        axiosInstance({
            url: getUrl(REQUEST_URL.projects),
            method: "PUT",
            data: body
        })
    );
};

export const deleteProject_API = async (query) => {
    return handleApiResponse(
        axiosInstance({
            url: getUrl(REQUEST_URL.projects, query),
            method: "DELETE"
        })
    );
};

export const getProjectDetail_API = async (query) => {
    return handleApiResponse(
        axiosInstance({
            url: getUrl(REQUEST_URL.projectDetail, query),
            method: "GET"
        })
    );
};

export const getActivityLogs_API = async (query) => {
    return handleApiResponse(
        axiosInstance({
            url: getUrl(REQUEST_URL.projectLogs, query),
            method: "GET"
        })
    );
};

export const assignProjectUsers_API = async (body) => {
    return handleApiResponse(
        axiosInstance({
            url: getUrl(REQUEST_URL.assignUsers),
            method: "POST",
            data: body
        })
    );
};